/* istanbul ignore file */
/* tslint:disable */
 
import type { Direction } from '../models/Direction';
import type { Discount } from '../models/Discount';
import type { GetDirectionsPrices } from '../models/GetDirectionsPrices';
import type { MedicalRisk } from '../models/MedicalRisk';
import type { MultiplePeriod } from '../models/MultiplePeriod';
import type { MultipleTariff } from '../models/MultipleTariff';
import type { PaginatedDirectionList } from '../models/PaginatedDirectionList';
import type { PaginatedDirectionWPriceList } from '../models/PaginatedDirectionWPriceList';
import type { PaginatedDiscountList } from '../models/PaginatedDiscountList';
import type { PaginatedMedicalBorderoList } from '../models/PaginatedMedicalBorderoList';
import type { PaginatedMedicalRiskList } from '../models/PaginatedMedicalRiskList';
import type { PaginatedTravelInsuranceListList } from '../models/PaginatedTravelInsuranceListList';
import type { PaginatedTravelInsuranceShortList } from '../models/PaginatedTravelInsuranceShortList';
import type { PatchedDirection } from '../models/PatchedDirection';
import type { PatchedDiscount } from '../models/PatchedDiscount';
import type { PatchedMedicalRisk } from '../models/PatchedMedicalRisk';
import type { PatchedMultiplePeriod } from '../models/PatchedMultiplePeriod';
import type { PatchedMultipleTariff } from '../models/PatchedMultipleTariff';
import type { PatchedTravelInsurance } from '../models/PatchedTravelInsurance';
import type { PatchedUnicPeriod } from '../models/PatchedUnicPeriod';
import type { PatchedUnicTariff } from '../models/PatchedUnicTariff';
import type { TravelInsurance } from '../models/TravelInsurance';
import type { TravelInsuranceCreate } from '../models/TravelInsuranceCreate';
import type { TravelInsuranceList } from '../models/TravelInsuranceList';
import type { UnicPeriod } from '../models/UnicPeriod';
import type { UnicTariff } from '../models/UnicTariff';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class MedicalService {

  /**
   * Bordero base for internal products built on Policy model.
   * @param agentId 
   * @param agentType * `employee` - employee
 * * `broker` - broker
 * * `website` - website
   * @param contractType * `order` - Order
 * * `modification` - Modification
 * * `cancel` - Cancel
   * @param endDate 
   * @param ordering Which field to use when ordering the results.
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: number, order__contractor__as_person__first_name, order__contractor__as_person__last_name, order__contractor__as_legalentity__name, order__contractor__as_legalentity__identification_number, order__contractor__as_person__identification_number
   * @param startDate 
   * @returns PaginatedMedicalBorderoList 
   * @throws ApiError
   */
  public static medicalBorderoList(
agentId?: number,
agentType?: 'broker' | 'employee' | 'website',
contractType?: 'cancel' | 'modification' | 'order',
endDate?: string,
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
startDate?: string,
): CancelablePromise<PaginatedMedicalBorderoList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/medical/bordero/',
      query: {
        'agent_id': agentId,
        'agent_type': agentType,
        'contract_type': contractType,
        'end_date': endDate,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
        'start_date': startDate,
      },
    });
  }

  /**
   * Bordero base for internal products built on Policy model.
   * @param agentId 
   * @param agentType * `employee` - employee
 * * `broker` - broker
 * * `website` - website
   * @param contractType * `order` - Order
 * * `modification` - Modification
 * * `cancel` - Cancel
   * @param endDate 
   * @param ordering Which field to use when ordering the results.
   * @param search Search fields: number, order__contractor__as_person__first_name, order__contractor__as_person__last_name, order__contractor__as_legalentity__name, order__contractor__as_legalentity__identification_number, order__contractor__as_person__identification_number
   * @param startDate 
   * @returns binary 
   * @throws ApiError
   */
  public static medicalBorderoXlsxRetrieve(
agentId?: number,
agentType?: 'broker' | 'employee' | 'website',
contractType?: 'cancel' | 'modification' | 'order',
endDate?: string,
ordering?: string,
search?: string,
startDate?: string,
): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/medical/bordero/xlsx/',
      query: {
        'agent_id': agentId,
        'agent_type': agentType,
        'contract_type': contractType,
        'end_date': endDate,
        'ordering': ordering,
        'search': search,
        'start_date': startDate,
      },
    });
  }

  /**
   * @param excludeIds comma separated list
   * @param ids comma separated list
   * @param ordering Ordering fields: created, modified, name, countries__len, sorting
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Caută direcțiile după denumirea localizată sau după codul țării.
   * @returns PaginatedDirectionList 
   * @throws ApiError
   */
  public static medicalDirectionsList(
excludeIds?: string,
ids?: string,
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedDirectionList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/medical/directions/',
      query: {
        'exclude_ids': excludeIds,
        'ids': ids,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
    });
  }

  /**
   * @param requestBody 
   * @returns Direction 
   * @throws ApiError
   */
  public static medicalDirectionsCreate(
requestBody: Direction,
): CancelablePromise<Direction> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/medical/directions/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this direction.
   * @returns Direction 
   * @throws ApiError
   */
  public static medicalDirectionsRetrieve(
id: number,
): CancelablePromise<Direction> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/medical/directions/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this direction.
   * @param requestBody 
   * @returns Direction 
   * @throws ApiError
   */
  public static medicalDirectionsPartialUpdate(
id: number,
requestBody?: PatchedDirection,
): CancelablePromise<Direction> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/medical/directions/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this direction.
   * @returns void 
   * @throws ApiError
   */
  public static medicalDirectionsDestroy(
id: number,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/medical/directions/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param excludeIds comma separated list
   * @param ids comma separated list
   * @param ordering Ordering fields: created, modified, name, countries__len, sorting
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Caută direcțiile după denumirea localizată sau după codul țării.
   * @returns PaginatedDirectionWPriceList 
   * @throws ApiError
   */
  public static medicalDirectionsPriceListList(
excludeIds?: string,
ids?: string,
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedDirectionWPriceList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/medical/directions/price-list/',
      query: {
        'exclude_ids': excludeIds,
        'ids': ids,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
    });
  }

  /**
   * @param ordering Ordering fields: category
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @returns PaginatedDiscountList 
   * @throws ApiError
   */
  public static medicalDiscountsList(
ordering?: string,
page?: number,
pageSize?: number,
): CancelablePromise<PaginatedDiscountList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/medical/discounts/',
      query: {
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
      },
    });
  }

  /**
   * @param category A unique value identifying this discount.
   * @returns Discount 
   * @throws ApiError
   */
  public static medicalDiscountsRetrieve(
category: string,
): CancelablePromise<Discount> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/medical/discounts/{category}/',
      path: {
        'category': category,
      },
    });
  }

  /**
   * @param category A unique value identifying this discount.
   * @param requestBody 
   * @returns Discount 
   * @throws ApiError
   */
  public static medicalDiscountsPartialUpdate(
category: string,
requestBody?: PatchedDiscount,
): CancelablePromise<Discount> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/medical/discounts/{category}/',
      path: {
        'category': category,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param ordering Ordering fields: days
   * @returns MultiplePeriod 
   * @throws ApiError
   */
  public static medicalMultiplePeriodsList(
ordering?: string,
): CancelablePromise<Array<MultiplePeriod>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/medical/multiple-periods/',
      query: {
        'ordering': ordering,
      },
    });
  }

  /**
   * @param requestBody 
   * @returns MultiplePeriod 
   * @throws ApiError
   */
  public static medicalMultiplePeriodsCreate(
requestBody: MultiplePeriod,
): CancelablePromise<MultiplePeriod> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/medical/multiple-periods/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param days A unique value identifying this multiple period.
   * @returns MultiplePeriod 
   * @throws ApiError
   */
  public static medicalMultiplePeriodsRetrieve(
days: number,
): CancelablePromise<MultiplePeriod> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/medical/multiple-periods/{days}/',
      path: {
        'days': days,
      },
    });
  }

  /**
   * @param days A unique value identifying this multiple period.
   * @param requestBody 
   * @returns MultiplePeriod 
   * @throws ApiError
   */
  public static medicalMultiplePeriodsPartialUpdate(
days: number,
requestBody?: PatchedMultiplePeriod,
): CancelablePromise<MultiplePeriod> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/medical/multiple-periods/{days}/',
      path: {
        'days': days,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param days A unique value identifying this multiple period.
   * @returns void 
   * @throws ApiError
   */
  public static medicalMultiplePeriodsDestroy(
days: number,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/medical/multiple-periods/{days}/',
      path: {
        'days': days,
      },
    });
  }

  /**
   * @param ordering Ordering fields: period
   * @returns MultipleTariff 
   * @throws ApiError
   */
  public static medicalMultipleTariffsList(
ordering?: string,
): CancelablePromise<Array<MultipleTariff>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/medical/multiple-tariffs/',
      query: {
        'ordering': ordering,
      },
    });
  }

  /**
   * @param requestBody 
   * @returns MultipleTariff 
   * @throws ApiError
   */
  public static medicalMultipleTariffsCreate(
requestBody: MultipleTariff,
): CancelablePromise<MultipleTariff> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/medical/multiple-tariffs/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this multiple tariff.
   * @returns MultipleTariff 
   * @throws ApiError
   */
  public static medicalMultipleTariffsRetrieve(
id: number,
): CancelablePromise<MultipleTariff> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/medical/multiple-tariffs/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this multiple tariff.
   * @param requestBody 
   * @returns MultipleTariff 
   * @throws ApiError
   */
  public static medicalMultipleTariffsPartialUpdate(
id: number,
requestBody?: PatchedMultipleTariff,
): CancelablePromise<MultipleTariff> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/medical/multiple-tariffs/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this multiple tariff.
   * @returns void 
   * @throws ApiError
   */
  public static medicalMultipleTariffsDestroy(
id: number,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/medical/multiple-tariffs/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param ordering Ordering fields: category
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @returns PaginatedMedicalRiskList 
   * @throws ApiError
   */
  public static medicalRiskCategoriesList(
ordering?: string,
page?: number,
pageSize?: number,
): CancelablePromise<PaginatedMedicalRiskList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/medical/risk-categories/',
      query: {
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
      },
    });
  }

  /**
   * @param category A unique value identifying this risk.
   * @returns MedicalRisk 
   * @throws ApiError
   */
  public static medicalRiskCategoriesRetrieve(
category: string,
): CancelablePromise<MedicalRisk> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/medical/risk-categories/{category}/',
      path: {
        'category': category,
      },
    });
  }

  /**
   * @param category A unique value identifying this risk.
   * @param requestBody 
   * @returns MedicalRisk 
   * @throws ApiError
   */
  public static medicalRiskCategoriesPartialUpdate(
category: string,
requestBody?: PatchedMedicalRisk,
): CancelablePromise<MedicalRisk> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/medical/risk-categories/{category}/',
      path: {
        'category': category,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param category Filtrează opțiunile de asigurare medicală după categorie.
 *
 * * `a` - Categoria A
 * * `b` - Categoria B
   * @param country Filtrează opțiunile de asigurare medicală care includ codul de țară transmis.
   * @param direction Filtrează opțiunile de asigurare medicală după ID-ul direcției.
   * @param excludeIds comma separated list
   * @param ids comma separated list
   * @param ordering Ordering fields: created, modified, direction__name, amount
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Caută opțiunile de asigurare medicală după numele direcției sau după codul țării.
   * @returns PaginatedTravelInsuranceListList 
   * @throws ApiError
   */
  public static medicalTravelInsurancesList(
category?: 'a' | 'b',
country?: string,
direction?: number,
excludeIds?: string,
ids?: string,
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedTravelInsuranceListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/medical/travel-insurances/',
      query: {
        'category': category,
        'country': country,
        'direction': direction,
        'exclude_ids': excludeIds,
        'ids': ids,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
    });
  }

  /**
   * @param requestBody 
   * @returns TravelInsuranceCreate 
   * @throws ApiError
   */
  public static medicalTravelInsurancesCreate(
requestBody: TravelInsuranceCreate,
): CancelablePromise<TravelInsuranceCreate> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/medical/travel-insurances/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this travel insurance.
   * @returns TravelInsuranceList 
   * @throws ApiError
   */
  public static medicalTravelInsurancesRetrieve(
id: number,
): CancelablePromise<TravelInsuranceList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/medical/travel-insurances/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this travel insurance.
   * @param requestBody 
   * @returns TravelInsurance 
   * @throws ApiError
   */
  public static medicalTravelInsurancesPartialUpdate(
id: number,
requestBody?: PatchedTravelInsurance,
): CancelablePromise<TravelInsurance> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/medical/travel-insurances/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this travel insurance.
   * @returns void 
   * @throws ApiError
   */
  public static medicalTravelInsurancesDestroy(
id: number,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/medical/travel-insurances/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Get available prices for selected directions
   * @param requestBody 
   * @param category Filtrează opțiunile de asigurare medicală după categorie.
 *
 * * `a` - Categoria A
 * * `b` - Categoria B
   * @param country Filtrează opțiunile de asigurare medicală care includ codul de țară transmis.
   * @param direction Filtrează opțiunile de asigurare medicală după ID-ul direcției.
   * @param excludeIds comma separated list
   * @param ids comma separated list
   * @param ordering Ordering fields: created, modified, direction__name, amount
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Caută opțiunile de asigurare medicală după numele direcției sau după codul țării.
   * @returns PaginatedTravelInsuranceShortList 
   * @throws ApiError
   */
  public static medicalTravelInsurancesDirectionsPricesCreate(
requestBody: GetDirectionsPrices,
category?: 'a' | 'b',
country?: string,
direction?: number,
excludeIds?: string,
ids?: string,
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedTravelInsuranceShortList> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/medical/travel-insurances/directions-prices/',
      query: {
        'category': category,
        'country': country,
        'direction': direction,
        'exclude_ids': excludeIds,
        'ids': ids,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param ordering Ordering fields: days
   * @returns UnicPeriod 
   * @throws ApiError
   */
  public static medicalUnicPeriodsList(
ordering?: string,
): CancelablePromise<Array<UnicPeriod>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/medical/unic-periods/',
      query: {
        'ordering': ordering,
      },
    });
  }

  /**
   * @param requestBody 
   * @returns UnicPeriod 
   * @throws ApiError
   */
  public static medicalUnicPeriodsCreate(
requestBody: UnicPeriod,
): CancelablePromise<UnicPeriod> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/medical/unic-periods/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param days A unique value identifying this unic period.
   * @returns UnicPeriod 
   * @throws ApiError
   */
  public static medicalUnicPeriodsRetrieve(
days: number,
): CancelablePromise<UnicPeriod> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/medical/unic-periods/{days}/',
      path: {
        'days': days,
      },
    });
  }

  /**
   * @param days A unique value identifying this unic period.
   * @param requestBody 
   * @returns UnicPeriod 
   * @throws ApiError
   */
  public static medicalUnicPeriodsPartialUpdate(
days: number,
requestBody?: PatchedUnicPeriod,
): CancelablePromise<UnicPeriod> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/medical/unic-periods/{days}/',
      path: {
        'days': days,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param days A unique value identifying this unic period.
   * @returns void 
   * @throws ApiError
   */
  public static medicalUnicPeriodsDestroy(
days: number,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/medical/unic-periods/{days}/',
      path: {
        'days': days,
      },
    });
  }

  /**
   * @param insurance 
   * @param ordering Ordering fields: period
   * @returns UnicTariff 
   * @throws ApiError
   */
  public static medicalUnicTariffsList(
insurance?: number,
ordering?: string,
): CancelablePromise<Array<UnicTariff>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/medical/unic-tariffs/',
      query: {
        'insurance': insurance,
        'ordering': ordering,
      },
    });
  }

  /**
   * @param requestBody 
   * @returns UnicTariff 
   * @throws ApiError
   */
  public static medicalUnicTariffsCreate(
requestBody: UnicTariff,
): CancelablePromise<UnicTariff> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/medical/unic-tariffs/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this unic tariff.
   * @returns UnicTariff 
   * @throws ApiError
   */
  public static medicalUnicTariffsRetrieve(
id: number,
): CancelablePromise<UnicTariff> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/medical/unic-tariffs/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this unic tariff.
   * @param requestBody 
   * @returns UnicTariff 
   * @throws ApiError
   */
  public static medicalUnicTariffsPartialUpdate(
id: number,
requestBody?: PatchedUnicTariff,
): CancelablePromise<UnicTariff> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/medical/unic-tariffs/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this unic tariff.
   * @returns void 
   * @throws ApiError
   */
  public static medicalUnicTariffsDestroy(
id: number,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/medical/unic-tariffs/{id}/',
      path: {
        'id': id,
      },
    });
  }

}
